import { useEffect, useRef, useState } from "react";

/**
 * 쿠키스토리 통합 드롭다운 컴포넌트
 * 갤러리 정렬 드롭다운과 동일한 모양 (pill + 캐러멜 보더 + 베이지 selected)
 *
 * Props:
 *  - value: 현재 선택값
 *  - onChange: (newValue) => void
 *  - options: [{ value, label, disabled? }]
 *  - placeholder: value가 없을 때 트리거에 표시할 텍스트
 *  - block: true면 풀너비 (폼 라벨 안에 사용)
 *  - size: "sm" | "md" (default: "sm" — 갤러리와 동일 32px)
 *  - className: 추가 클래스
 *  - disabled: 비활성화
 */
export default function CSSelect({
  value,
  onChange,
  options = [],
  placeholder = "",
  block = false,
  size = "sm",
  className = "",
  disabled = false,
  required = false,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function onDocClick(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener("mousedown", onDocClick);
      return () => document.removeEventListener("mousedown", onDocClick);
    }
  }, [open]);

  const current = options.find((o) => o.value === value);
  const label = current ? current.label : placeholder;

  function select(v, e) {
    if (e) e.stopPropagation();
    if (typeof onChange === "function") onChange(v);
    setOpen(false);
  }

  const wrapClass = [
    "cs-dropdown",
    block ? "cs-dropdown-block" : "",
    size === "md" ? "cs-dropdown-md" : "",
    disabled ? "cs-dropdown-disabled" : "",
    className,
  ].filter(Boolean).join(" ");

  return (
    <div className={wrapClass} ref={ref}>
      <button
        type="button"
        className={`cs-dropdown-trigger ${open ? "open" : ""} ${!current ? "is-placeholder" : ""}`}
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled) setOpen((v) => !v);
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-required={required}
        disabled={disabled}
      >
        <span className="cs-dropdown-label">{label}</span>
        <span className="cs-dropdown-caret" aria-hidden>▾</span>
      </button>
      {open && options.length > 0 && (
        <ul className="cs-dropdown-menu" role="listbox">
          {options.map((opt) => (
            <li
              key={String(opt.value)}
              role="option"
              aria-selected={value === opt.value}
              className={`cs-dropdown-option ${value === opt.value ? "selected" : ""} ${opt.disabled ? "disabled" : ""}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => !opt.disabled && select(opt.value, e)}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
